import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Mail, Clock } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import BackToTop from "./BackToTop";

// Landing page after a customer submits a cart-based quote request.
// Two flavors share this page:
//   - small_order (default) — delivery cart < $400, admin builds the
//     invoice with the right delivery fee.
//   - po_invoice — private customer (school / agency / non-profit) wants
//     an invoice to initiate an internal purchase order; admin builds
//     the invoice and the customer pays later once the PO is approved.
export const QuoteReceivedPage = () => {
  const [params] = useSearchParams();
  const quoteId = params.get("quote_id");
  const reason = params.get("reason");
  const isPO = reason === "po_invoice";
  const isItem = reason === "item_quote";
  return (
    <div className="App font-sans bg-white text-brand-charcoal antialiased">
      <Header />
      <main>
        <section className="pt-28 sm:pt-32 pb-24 bg-brand-cream min-h-[60vh] flex items-center">
          <div
            className="max-w-2xl mx-auto px-5 sm:px-6 lg:px-10 text-center w-full"
            data-testid="quote-received-success"
          >
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-green/15 text-brand-green">
              <CheckCircle2 className="h-8 w-8" />
            </span>
            <h1 className="mt-6 font-display font-black text-3xl sm:text-4xl tracking-tight text-brand-charcoal">
              {isPO ? "Invoice request received." : "Quote requested."}
            </h1>
            <p
              data-testid="quote-received-blurb"
              className="mt-3 text-[15px] text-brand-charcoal/70 leading-relaxed"
            >
              {isItem ? (
                <>
                  Thanks — pricing for this item is tailored to each event, so our team
                  will review your details and email you a <b>custom quote within 24 hours</b>.
                </>
              ) : isPO ? (
                <>
                  Thanks — we&apos;ll email you a <b>formal invoice within 24 hours</b> so
                  you can forward it internally to issue a purchase order. Once the
                  PO is in hand, reply to that email with it and we&apos;ll confirm
                  your event; payment is due by check or card on our standard terms.
                </>
              ) : (
                <>
                  Thanks — our catering team reviews small delivery orders personally.
                  You&apos;ll get a custom invoice with the right delivery fee by email
                  <b> within 24 hours</b>, and you can pay online or by check when it&apos;s ready.
                </>
              )}
            </p>
            {quoteId && (
              <p
                data-testid="quote-received-reference"
                className="mt-5 text-[11.5px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/55"
              >
                Reference: {quoteId.slice(0, 8).toUpperCase()}
              </p>
            )}
            <div className="mt-8 grid gap-3 sm:grid-cols-2 text-left">
              <div className="rounded-2xl bg-white border border-black/5 p-4">
                <div className="flex items-center gap-2 text-brand-teal">
                  <Mail className="h-4 w-4" />
                  <span className="text-[10.5px] font-bold uppercase tracking-[0.14em]">
                    Check your inbox
                  </span>
                </div>
                <p className="mt-1.5 text-[13px] text-brand-charcoal/70 leading-snug">
                  A confirmation is on the way to the email you provided.
                </p>
              </div>
              <div className="rounded-2xl bg-white border border-black/5 p-4">
                <div className="flex items-center gap-2 text-brand-gold-dark">
                  <Clock className="h-4 w-4" />
                  <span className="text-[10.5px] font-bold uppercase tracking-[0.14em]">
                    Within 24 hours
                  </span>
                </div>
                <p className="mt-1.5 text-[13px] text-brand-charcoal/70 leading-snug">
                  {isItem
                    ? "Your custom quote will arrive, ready to approve and pay."
                    : isPO
                    ? "Your formal invoice will arrive — ready to forward for the PO."
                    : "Your invoice will arrive with the total including delivery."}
                </p>
              </div>
            </div>
            <Link
              to="/"
              data-testid="quote-received-home-link"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
            >
              Back to Home <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default QuoteReceivedPage;
